import { CheckCircle2, Clock3, Copy, CreditCard, ExternalLink, Palette, Phone, Plus, Save, Settings, ShieldCheck, Smartphone, UserPlus, UsersRound, Wallet } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { DataTable } from '../../components/common/DataTable';
import { EmptyState } from '../../components/common/EmptyState';
import { FormField } from '../../components/common/FormField';
import { LoadingState } from '../../components/common/LoadingState';
import { Modal } from '../../components/common/Modal';
import { StatusBadge } from '../../components/common/StatusBadge';
import { createInvitation, getEmployees, getInvitations, getMembers, getRoles, updateTenantSettings, upsertUserDocument } from '../../firebase/firestore';
import { functions } from '../../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import { useTenant } from '../../hooks/useTenant';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { ROOT_DOMAIN } from '../../utils/hostname';
import { getThemePalette } from '../../utils/theme';
import academyHeroImage from '../../assets/public-academy-hero.png';
import { countryCurrencies } from '../../data/countryCurrencies';
import { phoneCountries } from '../../data/phoneCountries';
import { countryTimezones } from '../../data/countryTimezones';
import { normalizePhoneNumber } from '../../utils/validators';

const tenantTypes = ['private_instructor', 'school', 'university', 'college', 'training_center', 'virtual_academy', 'other'];
const switchableTenantTypes = tenantTypes.filter((type) => type !== 'university');
const colorPresets = ['#0f766e', '#2563eb', '#7c3aed', '#ea580c', '#be185d', '#0f172a'];
const publicTemplates = [
  {
    id: 'modern',
    name: 'Modern Admissions',
    description: 'Balanced layout with a polished hero, clear course cards and strong calls to action.',
    tone: 'Best for a clean, high-conversion public page.',
  },
  {
    id: 'classic',
    name: 'Classic Academic',
    description: 'A more traditional, confident presentation with a formal feel and stronger contrast.',
    tone: 'Best for schools and colleges that want a formal first impression.',
  },
  {
    id: 'minimal',
    name: 'Minimal Elegant',
    description: 'A light, quiet layout with softer styling and more whitespace around the content.',
    tone: 'Best for small academies that want a calm, premium look.',
  },
];

const freeTierFeatures = [
  {
    group: 'Workspace and public presence',
    items: [
      { name: 'SmartCampus academy workspace', included: true, detail: 'One place to manage your teaching business.' },
      { name: 'Academy profile, settings and branding', included: true, detail: 'Set your profile, logo, colour and public presentation.' },
      { name: 'SmartCampus subdomain', included: true, detail: 'Share your academy on a smartcampus.africa address.' },
      { name: 'Website builder and public academy page', included: true, detail: 'Create a simple home for your teaching business.' },
      { name: 'Custom domain', included: false, detail: 'Available on a paid package.' },
      { name: 'Multiple campuses and locations', included: false, detail: 'Available for larger academy packages.' },
    ],
  },
  {
    group: 'Admissions and learners',
    items: [
      { name: 'Course catalogue and public course pages', included: true, detail: 'List what you teach and let learners discover it.' },
      { name: 'Unlimited course sign-ups and applications', included: true, detail: 'Learners can sign up for your courses without a Free Tier student limit.' },
      { name: 'Admissions inbox and application tracking', included: true, detail: 'Keep new enquiries and sign-ups visible.' },
      { name: 'Student profiles and student portal', included: true, detail: 'Give learners access to their courses, classes and account.' },
      { name: 'Admission confirmations by email and SMS', included: true, detail: 'Included for up to 3 students per month.' },
      { name: 'WhatsApp registration and admissions messaging', included: false, detail: 'WhatsApp usage is not included in the Free tier.' },
    ],
  },
  {
    group: 'Teaching and learning',
    items: [
      { name: 'Course creation and learning library', included: true, detail: 'Organise your programmes and learner journeys.' },
      { name: 'Physical class scheduling', included: true, detail: 'Plan in-person lessons around your teaching week.' },
      { name: 'Attendance tracking', included: true, detail: 'Keep a record of learner participation.' },
      { name: 'Basic learner progress records', included: true, detail: 'Follow each learner as they move through a course.' },
      { name: 'Virtual learning', included: true, detail: 'Up to 3 students per month, with 45 minutes per day.' },
      { name: 'Class reminders by email', included: true, detail: 'Included for up to 3 students per month.' },
      { name: 'Virtual learning beyond the Free allowance', included: false, detail: 'Additional student time and capacity require a paid package.' },
      { name: 'Hybrid classes, breakout rooms and live collaboration tools', included: false, detail: 'Available on paid learning packages.' },
      { name: 'Student coursework materials: videos, audio and PDFs', included: false, detail: 'Uploading learning materials to student coursework is not included in the Free tier.' },
      { name: 'Assignments, assessments and certificates', included: false, detail: 'Advanced learning workflows are not included in the Free tier.' },
      { name: 'Firefly AI learning companion', included: false, detail: 'Available as a paid AI service.' },
      { name: 'Sell downloadable products and coaching offers', included: false, detail: 'Available on a paid package.' },
    ],
  },
  {
    group: 'Fees and payments',
    items: [
      { name: 'Fee structures and course prices', included: true, detail: 'Set the amounts learners are expected to pay.' },
      { name: 'Manual payment recording', included: true, detail: 'Record cash, bank and other payments you receive.' },
      { name: 'Invoices, statements and receipts', included: true, detail: 'Keep fee activity and learner balances clear.' },
      { name: 'Fee balances and payment history', included: true, detail: 'See what has been paid and what remains outstanding.' },
      { name: 'Fee reminders by email and SMS', included: true, detail: 'Included for up to 3 students per month.' },
      { name: 'Online payments through Lotus Pay, Paybill or Paystack', included: true, detail: 'Included for up to 3 students per month; provider transaction charges may still apply.' },
      { name: 'Online payment access beyond 3 students', included: false, detail: 'Available on a paid package.' },
      { name: 'Advanced finance operations', included: false, detail: 'Expenses, requisitions, approvals, budgets and reconciliation are not in the Free tier.' },
    ],
  },
  {
    group: 'Communication and insight',
    items: [
      { name: 'In-app notifications', included: true, detail: 'Keep important academy activity visible in the dashboard.' },
      { name: 'Email and SMS communication allowance', included: true, detail: 'Admission confirmations and fee reminders for up to 3 students per month.' },
      { name: 'Email and SMS beyond the 3-student allowance', included: false, detail: 'Available on a paid package.' },
      { name: 'WhatsApp messages', included: false, detail: 'WhatsApp messages are not included in the Free tier.' },
      { name: 'Advanced analytics and operational reports', included: false, detail: 'Available on a paid package.' },
      { name: 'Employee management, payroll and staff tools', included: false, detail: 'Available as your teaching business grows.' },
    ],
  },
];

const starterTierFeatures = freeTierFeatures.map((section) => ({
  ...section,
  items: section.items
    .filter((feature) => !['Virtual learning beyond the Free allowance', 'Online payment access beyond 3 students', 'Email and SMS beyond the 3-student allowance'].includes(feature.name))
    .map((feature) => {
      const details = {
        'Student profiles and student portal': 'Support an active learner base optimized for up to 20 students.',
        'Admission confirmations by email and SMS': 'Included for up to 20 students per month.',
        'Virtual learning': 'Up to 20 students, with 2 hours per student per day.',
        'Class reminders by email': 'Included for up to 20 students per month.',
        'Fee reminders by email and SMS': 'Included for up to 20 students per month.',
        'Online payments through Lotus Pay, Paybill or Paystack': 'Included for up to 20 students per month; provider transaction charges may still apply.',
        'Email and SMS communication allowance': 'Admission confirmations, fee reminders and class email reminders for up to 20 students per month.',
      };
      return details[feature.name] ? { ...feature, detail: details[feature.name] } : feature;
    }),
}));

const growthTierFeatures = starterTierFeatures.map((section) => ({
  ...section,
  items: section.items.map((feature) => {
    const changes = {
      'Student profiles and student portal': { detail: 'Support an active learner base optimized for up to 150 students.' },
      'Admission confirmations by email and SMS': { name: 'Admission confirmations by email, SMS and WhatsApp', detail: 'Included for up to 150 students per month.' },
      'Virtual learning': { detail: 'Up to 150 students, with 2 hours per student per day.' },
      'Class reminders by email': { name: 'Class reminders by email, SMS and WhatsApp', detail: 'All notification channels included for up to 150 students per month.' },
      'Fee reminders by email and SMS': { name: 'Fee reminders by email, SMS and WhatsApp', detail: 'All notification channels included for up to 150 students per month.' },
      'Online payments through Lotus Pay, Paybill or Paystack': { detail: 'Included for up to 150 students per month; provider transaction charges may still apply.' },
      'Email and SMS communication allowance': { name: 'Email, SMS and WhatsApp communication allowance', detail: 'WhatsApp is included across admission confirmations, fee reminders and class reminders for up to 150 students per month.' },
      'WhatsApp registration and admissions messaging': { included: true, detail: 'WhatsApp admissions messaging is included across the Growth notification allowance.' },
      'WhatsApp messages': { included: true, detail: 'WhatsApp is included on all notifications in the Growth package.' },
    };
    return changes[feature.name] ? { ...feature, ...changes[feature.name] } : feature;
  }),
}));

const proTierFeatures = growthTierFeatures.map((section) => ({
  ...section,
  items: section.items.map((feature) => {
    const changes = {
      'Student profiles and student portal': { detail: 'Support an active learner base optimized for up to 1,000 students.' },
      'Custom domain': { included: true, detail: 'Connect your own root domain or subdomain.' },
      'Multiple campuses and locations': { included: true, detail: 'Manage a larger operation across locations.' },
      'WhatsApp registration and admissions messaging': { included: true, detail: 'WhatsApp admissions messaging is included.' },
      'Virtual learning': { detail: 'Up to 1,000 students, with 2 hours per student per day.' },
      'Hybrid classes, breakout rooms and live collaboration tools': { included: true, detail: 'Run richer virtual and hybrid learning experiences.' },
      'Student coursework materials: videos, audio and PDFs': { included: true, detail: 'Upload videos, audio, PDFs and other learning materials to student coursework.' },
      'Assignments, assessments and certificates': { included: true, detail: 'Create assessments, track completion and recognise achievement.' },
      'Firefly AI learning companion': { included: true, detail: 'Give learners personal guidance and instructors deeper learning insight.' },
      'Sell downloadable products and coaching offers': { included: true, detail: 'Sell courses, coaching and downloadable learning products.' },
      'Online payments through Lotus Pay, Paybill or Paystack': { detail: 'Included for up to 1,000 students per month; provider transaction charges may still apply.' },
      'Advanced finance operations': { included: true, detail: 'Use expenses, requisitions, approvals, budgets and reconciliation tools.' },
      'Advanced analytics and operational reports': { included: true, detail: 'Use deeper analytics and reports to run your operation.' },
      'Employee management, payroll and staff tools': { included: true, detail: 'Manage staff, payroll and team operations.' },
      'Email, SMS and WhatsApp communication allowance': { name: 'Email, SMS and WhatsApp communications', detail: 'All notification channels included for up to 1,000 students per month.' },
      'WhatsApp messages': { included: true, detail: 'WhatsApp is included on all notifications.' },
    };
    return changes[feature.name] ? { ...feature, ...changes[feature.name] } : { ...feature, included: true };
  }),
}));

const tabs = [
  { id: 'general', label: 'General', icon: Settings, description: 'Core academy profile' },
  { id: 'fee-payment', label: 'School fee payment', icon: CreditCard, description: 'How students pay school fees' },
  { id: 'members & roles', label: 'Members & Roles', icon: ShieldCheck, description: 'Access and permissions' },
  { id: 'billing', label: 'Billing', icon: Wallet, description: 'Subscription and payments' },
  { id: 'edit academy', label: 'Edit academy', icon: Settings, description: 'Update academy details' },
  { id: 'branding', label: 'Branding', icon: Palette, description: 'Logo, color and theme' },
];

const DEFAULT_FEE_PAYMENT = {
  methods: ['cash', 'bank_transfer', 'mobile_money'],
  preferredMethod: '',
  preferredMethodSource: 'auto',
  bankName: '',
  accountName: '',
  accountNumber: '',
  branch: '',
  mobileMoneyProvider: '',
  mobileMoneyNumber: '',
  paymentInstructions: '',
  onlinePaymentLink: '',
  requireProof: false,
  lotusPay: { enabled: false, apiKey: '', clientId: '', clientSecret: '', paybill: '', mpesaPasskey: '', mpesaConsumerKey: '', mpesaConsumerSecret: '', accountNumber: '', callbackUrl: '' },
  paystack: { enabled: false, publicKey: '', secretKey: '', webhookSecret: '', callbackUrl: '' },
};

function getTenantTypeLabel(value) {
  return String(value || 'school').replace(/_/g, ' ');
}

function getCountryDetails(countryCode) {
  const entry = phoneCountries.find(([code]) => code === countryCode);
  if (!entry) return null;
  const [code, name, countryDialCode] = entry;
  return { countryCode: code, country: name, countryDialCode, currency: countryCurrencies[code] || 'USD', timezone: countryTimezones[code] || 'UTC' };
}

function getProfileCountryCode(profile) {
  const profileCode = profile?.phoneCountryCode || profile?.countryCode;
  if (getCountryDetails(profileCode)) return profileCode;
  const profileName = String(profile?.phoneCountry || profile?.country || '').toLowerCase();
  return phoneCountries.find(([, name]) => name.toLowerCase() === profileName)?.[0] || 'KE';
}

function getLocalPhoneNumber(phone, countryDialCode) {
  const digits = String(phone || '').replace(/\D/g, '');
  const dialCode = String(countryDialCode || '').replace(/\D/g, '');
  const withoutDialCode = dialCode && digits.startsWith(dialCode) ? digits.slice(dialCode.length) : digits;
  return withoutDialCode.replace(/^0+/, '');
}

function getInitialSettingsValues(activeTenant, profile, user) {
  const country = getCountryDetails(getProfileCountryCode(profile)) || getCountryDetails('KE');
  return {
    name: activeTenant?.name || '',
    type: activeTenant?.type || 'school',
    countryCode: country.countryCode,
    country: country.country,
    countryDialCode: country.countryDialCode,
    phone: getLocalPhoneNumber(profile?.phone || user?.phoneNumber, country.countryDialCode),
    timezone: country.timezone,
    currency: country.currency,
    publicTemplate: activeTenant?.settings?.publicTemplate || 'modern',
    publicHeroTitle: activeTenant?.settings?.publicWebsite?.heroTitle || '',
    publicHeroDescription: activeTenant?.settings?.publicWebsite?.heroDescription || '',
    publicHeroImageURL: activeTenant?.settings?.publicWebsite?.heroImageURL || '',
    publicAboutTitle: activeTenant?.settings?.publicWebsite?.aboutTitle || '',
    publicAboutDescription: activeTenant?.settings?.publicWebsite?.aboutDescription || '',
    primaryColor: activeTenant?.branding?.primaryColor || '',
    feePayment: { ...DEFAULT_FEE_PAYMENT, ...(activeTenant?.settings?.feePayment || {}), lotusPay: { ...DEFAULT_FEE_PAYMENT.lotusPay, ...(activeTenant?.settings?.feePayment?.lotusPay || {}) }, paystack: { ...DEFAULT_FEE_PAYMENT.paystack, ...(activeTenant?.settings?.feePayment?.paystack || {}) } },
  };
}

function SettingsAction({ icon: Icon, label, onClick, variant = 'secondary' }) {
  return (
    <Button type="button" variant={variant} icon={Icon} onClick={onClick} className="settingsHeroAction">
      {label}
    </Button>
  );
}

function FeatureAccessList({ tierLabel = 'Private instructor · Free tier', heading = 'What you can use for free', intro = 'Start with the essentials for teaching one-to-one or a small group. Upgrade when you need more capacity, automation or advanced operations.', features = freeTierFeatures }) {
  return (
    <div className="settingsBillingFeatures" aria-label="Private instructor Free tier features">
      <div className="settingsBillingFeaturesHeader">
        <div>
          <p className="settingsSectionLabel">{tierLabel}</p>
          <h3>{heading}</h3>
          <p>{intro}</p>
        </div>
        <div className="settingsBillingFeatureLegend" aria-label="Feature access legend"><span><i className="is-included">✓</i> Included</span><span><i className="is-excluded">×</i> Not included</span></div>
      </div>
      <div className="settingsBillingFeatureGroups">
        {features.map((section) => (
          <section key={section.group} className="settingsBillingFeatureGroup">
            <h4>{section.group}</h4>
            <div className="settingsBillingFeatureRows">
              {section.items.map((feature) => (
                <div className="settingsBillingFeatureRow" key={feature.name}>
                  <i className={feature.included ? 'is-included' : 'is-excluded'} aria-label={feature.included ? 'Included' : 'Not included'}>{feature.included ? '✓' : '×'}</i>
                  <strong>{feature.name}</strong>
                  <span>{feature.detail}</span>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export function SettingsPage({ mode = 'settings' }) {
  const isSiteBuilder = mode === 'site-builder';
  const { activeTenant, activeTenantId, refreshTenant, role } = useTenant();
  const [searchParams] = useSearchParams();
  const { notify } = useToast();
  const { user, profile, refreshProfile } = useAuth();
  const [activeTab, setActiveTab] = useState(isSiteBuilder ? 'branding' : (searchParams.get('tab') || 'general'));
  const [processorTab, setProcessorTab] = useState('lotusPay');
  const [saving, setSaving] = useState(false);
  const [paymentTest, setPaymentTest] = useState({ tab: '', fingerprint: '', status: 'idle', error: '' });
  const [editingPaystackCredentials, setEditingPaystackCredentials] = useState(false);
  const [paystackCredentialsSaved, setPaystackCredentialsSaved] = useState(false);
  const [lotusPayCredentialsSaved, setLotusPayCredentialsSaved] = useState(false);
  const [paybillCredentialsSaved, setPaybillCredentialsSaved] = useState(false);
  const [editingLotusPayCredentials, setEditingLotusPayCredentials] = useState(false);
  const [lotusPayTestModalOpen, setLotusPayTestModalOpen] = useState(false);
  const [lotusPayTestPhone, setLotusPayTestPhone] = useState(user?.phoneNumber || '');
  const [lotusPayTestStage, setLotusPayTestStage] = useState('idle');
  const [paybillTestModalOpen, setPaybillTestModalOpen] = useState(false);
  const [paybillTestPhone, setPaybillTestPhone] = useState(user?.phoneNumber || '');
  const [paybillTestStage, setPaybillTestStage] = useState('idle');
  const [editingAcademy, setEditingAcademy] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamEmployees, setTeamEmployees] = useState([]);
  const [teamRoles, setTeamRoles] = useState([]);
  const [teamInvitations, setTeamInvitations] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamSaving, setTeamSaving] = useState(false);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [teamValues, setTeamValues] = useState({ employeeId: '', roleId: 'instructor' });
  const [billingPlan, setBillingPlan] = useState('free');
  const [values, setValues] = useState(() => getInitialSettingsValues(activeTenant, profile, user));

  const activePaymentMethods = useMemo(() => {
    const feePayment = values.feePayment;
    return [
      (feePayment.lotusPay.configured || lotusPayCredentialsSaved) ? 'Lotus Pay' : null,
      feePayment.paystack.enabled && feePayment.paystack.publicKey && (feePayment.paystack.secretKey || paystackCredentialsSaved) ? 'Paystack' : null,
      (feePayment.lotusPay.paybillConfigured || paybillCredentialsSaved) ? 'M-Pesa Paybill' : null,
    ].filter(Boolean);
  }, [values.feePayment, paystackCredentialsSaved, lotusPayCredentialsSaved, paybillCredentialsSaved]);

  function paymentMethodIsConfigured(methodId) {
    const feePayment = values.feePayment;
    if (methodId === 'lotusPay') return Boolean(feePayment.lotusPay.configured || lotusPayCredentialsSaved);
    if (methodId === 'paystack') return Boolean(feePayment.paystack.enabled && feePayment.paystack.publicKey && (feePayment.paystack.secretKey || paystackCredentialsSaved));
    if (methodId === 'paybill') return Boolean(feePayment.lotusPay.paybillConfigured || paybillCredentialsSaved);
    return false;
  }

  useEffect(() => {
    setValues(getInitialSettingsValues(activeTenant, profile, user));
    setPaystackCredentialsSaved(Boolean(activeTenant?.settings?.feePayment?.paystack?.secretCiphertext));
    setLotusPayCredentialsSaved(Boolean(activeTenant?.settings?.feePayment?.lotusPay?.apiKeyCiphertext || activeTenant?.settings?.feePayment?.lotusPay?.configured));
    setPaybillCredentialsSaved(Boolean(activeTenant?.settings?.feePayment?.lotusPay?.paybillCredentialsCiphertext || activeTenant?.settings?.feePayment?.lotusPay?.paybillConfigured));
    setEditingLotusPayCredentials(false);
    setEditingPaystackCredentials(false);
  }, [activeTenant, profile, user]);

  const loadTeam = useCallback(async () => {
    if (!activeTenantId) {
      setTeamMembers([]);
      setTeamEmployees([]);
      setTeamRoles([]);
      setTeamInvitations([]);
      setTeamLoading(false);
      return;
    }
    setTeamLoading(true);
    try {
      const [members, employees, roles, invitations] = await Promise.all([
        getMembers(activeTenantId),
        getEmployees(activeTenantId),
        getRoles(activeTenantId),
        getInvitations(activeTenantId),
      ]);
      setTeamMembers(members);
      setTeamEmployees(employees);
      setTeamRoles(roles);
      setTeamInvitations(invitations);
    } catch (error) {
      console.error('Unable to load members and roles', error);
      notify('Unable to load members and roles. Try again.', 'error');
    } finally {
      setTeamLoading(false);
    }
  }, [activeTenantId, notify]);

  useEffect(() => {
    if (activeTab !== 'members & roles') return;
    loadTeam().catch((error) => console.error(error));
  }, [activeTab, loadTeam]);

  const teamRoleName = useCallback((roleId) => teamRoles.find((item) => item.id === roleId)?.name || roleId || 'Not assigned', [teamRoles]);
  const teamRows = useMemo(() => {
    const currentEmail = String(user?.email || '').trim().toLowerCase();
    const rows = teamMembers.map((member) => ({ ...member, isCurrentUser: member.userId === user?.uid || (currentEmail && String(member.email || '').trim().toLowerCase() === currentEmail) }));
    if (!rows.some((member) => member.isCurrentUser) && user) {
      rows.unshift({
        id: user.uid,
        userId: user.uid,
        displayName: user.displayName || user.email || 'Signed-in user',
        email: user.email || '',
        roleId: role?.id || role?.key || 'owner',
        status: 'active',
        isCurrentUser: true,
      });
    }
    const pendingRows = teamInvitations.filter((invitation) => invitation.status === 'pending').map((invitation) => ({
      ...invitation,
      id: `invitation-${invitation.id}`,
      displayName: invitation.employeeName || invitation.email || 'Invited member',
      status: 'pending',
      isInvitation: true,
    }));
    return [...rows, ...pendingRows];
  }, [role?.id, role?.key, teamInvitations, teamMembers, user]);
  const assignableRoles = useMemo(() => teamRoles.filter((item) => !['owner', 'student'].includes(item.id)), [teamRoles]);
  const assignedEmails = useMemo(() => new Set(teamRows.map((member) => String(member.email || '').trim().toLowerCase()).filter(Boolean)), [teamRows]);
  const availableEmployees = useMemo(() => teamEmployees.filter((employee) => employee.status !== 'inactive' && employee.email && !assignedEmails.has(String(employee.email).trim().toLowerCase())), [assignedEmails, teamEmployees]);

  function openTeamModal() {
    setTeamValues({ employeeId: availableEmployees[0]?.id || '', roleId: assignableRoles[0]?.id || 'instructor' });
    setTeamModalOpen(true);
  }

  async function addEmployeeMember(event) {
    event.preventDefault();
    if (!activeTenantId || teamSaving) return;
    const employee = availableEmployees.find((item) => item.id === teamValues.employeeId);
    if (!employee || !teamValues.roleId) {
      notify('Choose an employee and role before adding a member.', 'error');
      return;
    }
    setTeamSaving(true);
    try {
      await createInvitation(activeTenantId, user?.uid, {
        email: employee.email.trim(),
        roleId: teamValues.roleId,
        employeeId: employee.id,
        employeeName: employee.fullName || '',
        employeePhone: employee.phone || '',
      });
      notify(`${employee.fullName || employee.email} was added as a pending ${teamRoleName(teamValues.roleId)} member.`);
      setTeamModalOpen(false);
      await loadTeam();
    } catch (error) {
      console.error('Unable to add employee member', error);
      notify('Unable to add this member. Try again.', 'error');
    } finally {
      setTeamSaving(false);
    }
  }

  const academySubdomain = useMemo(
    () => (activeTenant?.slug ? `${activeTenant.slug}.${ROOT_DOMAIN}` : 'academy.smartcampus.africa'),
    [activeTenant?.slug],
  );
  const academySubdomainURL = useMemo(() => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost');
    const host = isLocal ? `${activeTenant?.slug || 'academy'}.localhost` : academySubdomain;
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${window.location.protocol}//${host}${port}`;
  }, [academySubdomain, activeTenant?.slug]);

  async function persistSettings(nextValues = values, successMessage = 'Organization settings updated.') {
    if (!activeTenantId) return false;
    setSaving(true);
    try {
      await updateTenantSettings(activeTenantId, nextValues);
      await refreshTenant();
      notify(successMessage);
      return true;
    } catch (error) {
      console.error(error);
      notify('Unable to update settings. Try again.', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function onSubmit(event) {
    event.preventDefault();
    if (activeTab === 'edit academy') {
      const country = getCountryDetails(values.countryCode);
      if (!country || !user) {
        notify('Choose a valid country before saving academy details.', 'error');
        return;
      }
      const normalizedPhone = normalizePhoneNumber(values.phone, country.countryDialCode);
      if (!normalizedPhone || normalizedPhone.replace(/\D/g, '').length < 8) {
        notify('Enter a valid phone number before saving academy details.', 'error');
        return;
      }
      // Country is authoritative. Always derive these values again at save time
      // so timezone/currency cannot be changed independently or become stale.
      const nextValues = { ...values, ...country, phone: normalizedPhone };
      setValues(nextValues);
      const saved = await persistSettings(nextValues);
      if (!saved) return;
      try {
        await upsertUserDocument(user, {
          phone: normalizedPhone,
          country: country.country,
          countryCode: country.countryCode,
          phoneCountry: country.country,
          phoneCountryCode: country.countryCode,
          phoneCountryDialCode: country.countryDialCode,
          timezone: country.timezone,
          currency: country.currency,
        });
        await refreshProfile();
      } catch (error) {
        console.error('Unable to update academy country profile', error);
        notify('Academy settings were saved, but the account country could not be updated. Try again.', 'error');
        return;
      }
      if (activeTab === 'edit academy') {
        setEditingAcademy(false);
        setActiveTab('general');
      }
      return;
    }
    const saved = await persistSettings();
    if (saved && activeTab === 'edit academy') {
      setEditingAcademy(false);
      setActiveTab('general');
    }
  }

  async function saveFeePaymentSettings(label) {
    const savedMethodId = { 'Lotus Pay': 'lotusPay', Paystack: 'paystack', 'M-Pesa Paybill': 'paybill' }[label];
    if (label === 'Preferred payment method') {
      if (!paymentMethodIsConfigured(values.feePayment.preferredMethod)) {
        notify('Test and save this payment method before making it the default.', 'error');
        return;
      }
      await persistSettings({ ...values, feePayment: { ...values.feePayment, preferredMethodSource: 'manual' } }, 'Preferred payment method updated.');
      return;
    }
    const shouldAutoSelect = Boolean(savedMethodId);
    const nextFeePayment = shouldAutoSelect ? { ...values.feePayment, preferredMethod: savedMethodId, preferredMethodSource: 'auto' } : values.feePayment;
    if (shouldAutoSelect) setValues((current) => ({ ...current, feePayment: nextFeePayment }));
    if (label === 'Paystack') {
      try {
        const savePaystackCredentials = httpsCallable(functions, 'savePaystackCredentials');
        await savePaystackCredentials({
          tenantId: activeTenantId,
          publicKey: values.feePayment.paystack.publicKey,
          secretKey: values.feePayment.paystack.secretKey,
          preferredMethod: nextFeePayment.preferredMethod,
          preferredMethodSource: nextFeePayment.preferredMethodSource,
        });
        await persistSettings({ ...values, skipFeePayment: true }, `${label} settings saved securely.`);
        setPaystackCredentialsSaved(true);
        setEditingPaystackCredentials(false);
      } catch (error) {
        console.error(error);
        notify(error.message || 'Unable to securely save Paystack settings.', 'error');
      }
      return;
    }
    if (label === 'Lotus Pay') {
      try {
        const saveLotusPayCredentials = httpsCallable(functions, 'saveLotusPayCredentials');
        await saveLotusPayCredentials({
          tenantId: activeTenantId,
          apiKey: values.feePayment.lotusPay.apiKey,
          preferredMethod: nextFeePayment.preferredMethod,
          preferredMethodSource: nextFeePayment.preferredMethodSource,
        });
        await persistSettings({ ...values, feePayment: { ...nextFeePayment, lotusPay: { ...nextFeePayment.lotusPay, apiKey: '', configured: true } }, skipFeePayment: true }, `${label} settings saved securely.`);
        setValues((current) => ({ ...current, feePayment: { ...current.feePayment, ...nextFeePayment, lotusPay: { ...nextFeePayment.lotusPay, apiKey: '', configured: true } } }));
        setLotusPayCredentialsSaved(true);
        setEditingLotusPayCredentials(false);
      } catch (error) {
        console.error(error);
        notify(error.message || 'Unable to securely save Lotus Pay settings.', 'error');
      }
      return;
    }
    if (label === 'M-Pesa Paybill') {
      try {
        const savePaybillCredentials = httpsCallable(functions, 'saveMpesaPaybillCredentials');
        await savePaybillCredentials({ tenantId: activeTenantId, paybill: values.feePayment.lotusPay.paybill, passkey: values.feePayment.lotusPay.mpesaPasskey, consumerKey: values.feePayment.lotusPay.mpesaConsumerKey, consumerSecret: values.feePayment.lotusPay.mpesaConsumerSecret, preferredMethod: nextFeePayment.preferredMethod, preferredMethodSource: nextFeePayment.preferredMethodSource });
        await persistSettings({ ...values, feePayment: { ...nextFeePayment, lotusPay: { ...nextFeePayment.lotusPay, mpesaPasskey: '', mpesaConsumerKey: '', mpesaConsumerSecret: '', paybillConfigured: true } }, skipFeePayment: true }, `${label} settings saved securely.`);
        setValues((current) => ({ ...current, feePayment: { ...current.feePayment, ...nextFeePayment, lotusPay: { ...nextFeePayment.lotusPay, mpesaPasskey: '', mpesaConsumerKey: '', mpesaConsumerSecret: '', paybillConfigured: true } } }));
        setPaybillCredentialsSaved(true);
      } catch (error) {
        console.error(error);
        notify(error.message || 'Unable to securely save M-Pesa Paybill settings.', 'error');
      }
      return;
    }
    const safePaystack = { ...(nextFeePayment.paystack || {}) };
    delete safePaystack.secretKey;
    const savedCiphertext = activeTenant?.settings?.feePayment?.paystack?.secretCiphertext;
    const safeFeePayment = { ...nextFeePayment, paystack: { ...safePaystack, ...(savedCiphertext ? { secretCiphertext: savedCiphertext } : {}) } };
    await persistSettings({ ...values, feePayment: safeFeePayment }, `${label} settings saved.`);
  }

  function updateFeePayment(field, value) {
    setValues((current) => ({ ...current, feePayment: { ...current.feePayment, [field]: value, ...(field === 'preferredMethod' ? { preferredMethodSource: 'manual' } : {}) } }));
  }

  function updateProcessor(processor, field, value) {
    setValues((current) => ({ ...current, feePayment: { ...current.feePayment, [processor]: { ...current.feePayment[processor], [field]: value } } }));
  }

  function getPaymentFingerprint(tab) {
    const feePayment = values.feePayment;
    const fields = tab === 'lotusPay'
      ? { apiKey: feePayment.lotusPay.apiKey }
      : tab === 'paystack'
        ? { enabled: feePayment.paystack.enabled, publicKey: feePayment.paystack.publicKey, secretKey: editingPaystackCredentials ? feePayment.paystack.secretKey : '' }
        : tab === 'paybill'
          ? { paybill: feePayment.lotusPay.paybill, mpesaPasskey: feePayment.lotusPay.mpesaPasskey, mpesaConsumerKey: feePayment.lotusPay.mpesaConsumerKey, mpesaConsumerSecret: feePayment.lotusPay.mpesaConsumerSecret }
          : {};
    return JSON.stringify(fields);
  }

  function getPaymentTestFields(tab) {
    const feePayment = values.feePayment;
    if (tab === 'lotusPay' && !(lotusPayCredentialsSaved && !editingLotusPayCredentials)) return [feePayment.lotusPay.apiKey];
    if (tab === 'paystack') return editingPaystackCredentials ? [feePayment.paystack.publicKey, feePayment.paystack.secretKey] : [feePayment.paystack.publicKey];
    if (tab === 'paybill' && !(paybillCredentialsSaved && !editingLotusPayCredentials)) return [feePayment.lotusPay.paybill, feePayment.lotusPay.mpesaPasskey, feePayment.lotusPay.mpesaConsumerKey, feePayment.lotusPay.mpesaConsumerSecret];
    if (tab === 'paybill') return [];
    return [];
  }

  function openLotusPayTestModal() {
    setLotusPayTestPhone(user?.phoneNumber || '');
    setPaymentTest({ tab: 'lotusPay', fingerprint: '', status: 'idle', error: '' });
    setLotusPayTestStage('idle');
    setLotusPayTestModalOpen(true);
  }

  async function submitLotusPayTest(event) {
    event.preventDefault();
    if (!lotusPayTestPhone.trim()) return;
    await testPaymentMethod('lotusPay', 'Lotus Pay', lotusPayTestPhone.trim());
  }

  function openPaybillTestModal() {
    setPaybillTestPhone(user?.phoneNumber || '');
    setPaymentTest({ tab: 'paybill', fingerprint: '', status: 'idle', error: '' });
    setPaybillTestStage('idle');
    setPaybillTestModalOpen(true);
  }

  async function submitPaybillTest(event) {
    event.preventDefault();
    if (!paybillTestPhone.trim()) return;
    await testPaymentMethod('paybill', 'M-Pesa Paybill', paybillTestPhone.trim());
  }

  function loadPaystackCheckout() {
    if (window.PaystackPop) return Promise.resolve(window.PaystackPop);
    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[data-paystack-inline]');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.PaystackPop));
        existingScript.addEventListener('error', () => reject(new Error('Paystack checkout could not be loaded.')));
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v2/inline.js';
      script.async = true;
      script.dataset.paystackInline = 'true';
      script.onload = () => resolve(window.PaystackPop);
      script.onerror = () => reject(new Error('Paystack checkout could not be loaded.'));
      document.body.appendChild(script);
    });
  }

  async function openPaystackTestCheckout() {
    const PaystackPop = await loadPaystackCheckout();
    if (!PaystackPop) throw new Error('Paystack checkout is unavailable in this browser.');
    const popup = new PaystackPop();
    popup.checkout({
      key: values.feePayment.paystack.publicKey,
      email: user?.email || 'academy-test@smartcampus.africa',
      amount: 1000,
      currency: 'KES',
      metadata: { custom_fields: [{ display_name: 'Purpose', variable_name: 'purpose', value: 'Paystack connection test' }] },
      onCancel: () => notify('Paystack test checkout closed. No payment was recorded.'),
      onSuccess: () => notify('Paystack checkout completed. This test payment is not recorded as a student fee.'),
    });
  }

  async function testPaymentMethod(tab, label, testPhoneNumber = '') {
    if (tab === 'paybill' && !testPhoneNumber) {
      openPaybillTestModal();
      return;
    }
    const requiredFields = getPaymentTestFields(tab);
    if (requiredFields.some((field) => !String(field || '').trim())) {
      const message = `Enter all required ${label} details before testing.`;
      setPaymentTest({ tab, fingerprint: '', status: 'error', error: message });
      notify(message, 'error');
      return;
    }
    if (tab === 'paystack') {
      const { publicKey, secretKey } = values.feePayment.paystack;
      if (!String(publicKey).startsWith('pk_live_') || (editingPaystackCredentials && !String(secretKey).startsWith('sk_live_'))) {
        const error = 'Paystack only accepts live credentials here. Use a public key starting with pk_live_ and a secret key starting with sk_live_.';
        setPaymentTest({ tab, fingerprint: '', status: 'error', error });
        notify(error, 'error');
        return;
      }
    }
    if (tab === 'lotusPay') {
      const phoneNumber = testPhoneNumber;
      if (!phoneNumber) {
        setPaymentTest({ tab, fingerprint: '', status: 'error', error: 'A phone number is required to run the Lotus Pay test.' });
        return;
      }
      try {
        setLotusPayTestStage('sending');
        setPaymentTest({ tab, fingerprint: getPaymentFingerprint(tab), status: 'testing', error: '' });
        const startTest = httpsCallable(functions, 'startLotusPayConnectionTest');
        const started = await startTest({ tenantId: activeTenantId, apiKey: values.feePayment.lotusPay.apiKey, phoneNumber, contactName: user?.displayName || user?.email || 'SmartCampus tester' });
        const collectionId = started.data?.collectionId;
        if (!collectionId) throw new Error('Lotus Pay did not return a test collection.');
        setLotusPayTestStage('confirming');
        notify('KES 5 prompt sent. Approve it on your phone; we’re confirming the payment automatically.');
        for (let attempt = 0; attempt < 20; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
          try {
            const verifyTest = httpsCallable(functions, 'verifyLotusPayConnectionTest');
            const verified = await verifyTest({ tenantId: activeTenantId, collectionId, apiKey: values.feePayment.lotusPay.apiKey });
            if (verified.data?.ok) {
              setLotusPayTestStage('success');
              setPaymentTest({ tab, fingerprint: getPaymentFingerprint(tab), status: 'passed', error: '' });
              notify('Lotus Pay payment verified successfully. You can save this method.');
              return;
            }
          } catch (error) {
            console.warn('Lotus Pay test is still pending', error);
          }
        }
        throw new Error('The payment could not be confirmed within one minute. If you approved the prompt, check your Lotus Pay dashboard before trying again.');
      } catch (error) {
        const message = error.message || 'Lotus Pay connection could not be verified.';
        setLotusPayTestStage('error');
        setPaymentTest({ tab, fingerprint: '', status: 'error', error: message });
        notify(message, 'error');
        return;
      }
    }
    if (tab === 'paybill') {
      if (!testPhoneNumber) {
        setPaymentTest({ tab, fingerprint: '', status: 'error', error: 'A phone number is required to run the M-Pesa Paybill test.' });
        return;
      }
      try {
        setPaybillTestStage('sending');
        setPaymentTest({ tab, fingerprint: getPaymentFingerprint(tab), status: 'testing', error: '' });
        const startTest = httpsCallable(functions, 'startMpesaPaybillConnectionTest');
        const started = await startTest({ tenantId: activeTenantId, paybill: values.feePayment.lotusPay.paybill, passkey: values.feePayment.lotusPay.mpesaPasskey, consumerKey: values.feePayment.lotusPay.mpesaConsumerKey, consumerSecret: values.feePayment.lotusPay.mpesaConsumerSecret, phoneNumber: testPhoneNumber, contactName: user?.displayName || user?.email || 'SmartCampus tester' });
        const checkoutRequestId = started.data?.checkoutRequestId;
        if (!checkoutRequestId) throw new Error('Safaricom did not return a test payment reference.');
        setPaybillTestStage('confirming');
        notify('KES 5 M-Pesa prompt sent. Approve it on your phone; we’re confirming the payment automatically.');
        for (let attempt = 0; attempt < 20; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
          try {
            const verifyTest = httpsCallable(functions, 'verifyMpesaPaybillConnectionTest');
            const verified = await verifyTest({ tenantId: activeTenantId, checkoutRequestId, paybill: values.feePayment.lotusPay.paybill, passkey: values.feePayment.lotusPay.mpesaPasskey, consumerKey: values.feePayment.lotusPay.mpesaConsumerKey, consumerSecret: values.feePayment.lotusPay.mpesaConsumerSecret });
            if (verified.data?.ok) {
              setPaybillTestStage('success');
              setPaymentTest({ tab, fingerprint: getPaymentFingerprint(tab), status: 'passed', error: '' });
              notify('M-Pesa Paybill payment verified successfully. You can save this method.');
              return;
            }
          } catch (error) { console.warn('M-Pesa Paybill test is still pending', error); }
        }
        throw new Error('The M-Pesa payment could not be confirmed within one minute. If you approved the prompt, wait a moment and try again.');
      } catch (error) {
        const message = error.message || 'M-Pesa Paybill connection could not be verified.';
        setPaybillTestStage('error');
        setPaymentTest({ tab, fingerprint: '', status: 'error', error: message });
        notify(message, 'error');
      }
      return;
    }
    setPaymentTest({ tab, fingerprint: getPaymentFingerprint(tab), status: 'testing', error: '' });
    if (tab === 'paystack') {
      try {
        await openPaystackTestCheckout();
      } catch (error) {
        const message = error.message || 'Paystack checkout could not be opened.';
        setPaymentTest({ tab, fingerprint: '', status: 'error', error: message });
        notify(message, 'error');
        return;
      }
    } else {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    setPaymentTest({ tab, fingerprint: getPaymentFingerprint(tab), status: 'passed', error: '' });
    notify(tab === 'paystack' ? 'Paystack checkout opened successfully. You can save this method.' : `${label} is ready to save.`);
  }

  function paymentTestPassed(tab) {
    return paymentTest.tab === tab && paymentTest.status === 'passed' && paymentTest.fingerprint === getPaymentFingerprint(tab);
  }

  async function copyAcademyLink() {
    await navigator.clipboard.writeText(academySubdomainURL);
    notify('Academy link copied.');
  }

  async function copyAcademySubdomain() {
    await navigator.clipboard.writeText(academySubdomain);
    notify('Subdomain copied.');
  }

  async function savePublicTemplate(templateId) {
    const nextValues = { ...values, publicTemplate: templateId };
    setValues(nextValues);
    await persistSettings(nextValues, `${publicTemplates.find((template) => template.id === templateId)?.name || 'Template'} selected.`);
  }

  function previewTemplate(templateId) {
    const previewUrl = `${window.location.origin}/academy/${activeTenant?.slug || ''}?template=${templateId}&preview=1`;
    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  }

  const primaryColor = values.primaryColor || activeTenant?.branding?.primaryColor || '#0f766e';
  const brandPalette = useMemo(() => getThemePalette(primaryColor), [primaryColor]);
  const orderedPhoneCountries = useMemo(() => {
    const selectedCountryCode = values.countryCode || getProfileCountryCode(profile);
    return [
      ...phoneCountries.filter(([code]) => code === selectedCountryCode),
      ...phoneCountries.filter(([code]) => code !== selectedCountryCode),
    ];
  }, [profile, values.countryCode]);
  const currentCountry = getCountryDetails(values.countryCode);
  const visibleTabs = tabs.filter((tab) => (isSiteBuilder ? tab.id === 'branding' : tab.id !== 'branding' && (tab.id !== 'edit academy' || editingAcademy)));
  const tabsById = new Map(visibleTabs.map((tab) => [tab.id, tab]));

  return (
    <div className={`page-stack settingsPage ${isSiteBuilder ? 'siteBuilderDashboardPage' : ''}`}>
      <section className="settingsHero">
        <div className="settingsHeroCopy">
          <p className="settingsKicker">{isSiteBuilder ? 'Website experience' : 'Organization'}</p>
          <h1>{isSiteBuilder ? 'Site Builder' : 'Settings'}</h1>
          <p>{isSiteBuilder ? 'Create, customise and publish the website your learners see first.' : 'Shape the academy profile, access and public link in one place.'}</p>
          <div className="settingsHeroBadges">
            <span>{getTenantTypeLabel(activeTenant?.type)}</span>
            <span>{activeTenant?.settings?.timezone || 'Africa/Nairobi'}</span>
            <span>{activeTenant?.settings?.currency || 'KES'}</span>
          </div>
        </div>

        <div className="settingsHeroActions">
          <SettingsAction
            label="Copy Academy Link"
            icon={Copy}
            onClick={copyAcademyLink}
            variant="secondary"
          />
          <SettingsAction
            label="Open Academy"
            icon={ExternalLink}
            onClick={() => window.open(academySubdomainURL, '_blank', 'noopener,noreferrer')}
            variant="primary"
          />
        </div>
      </section>

      <section className="settingsWorkspace">
        <aside className="settingsRail">
          <div className="settingsRailCard">
            <p className="settingsRailLabel">Sections</p>
            <div className="settingsTabs">
              {visibleTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={activeTab === tab.id ? 'active' : ''}
                    aria-pressed={activeTab === tab.id}
                    onClick={() => { setActiveTab(tab.id); if (tab.id === 'general') setEditingAcademy(false); }}
                  >
                    <Icon size={16} />
                    <span>
                      <strong>{tab.label}</strong>
                      <small>{tab.description}</small>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        <section className="settingsMain">
          <div className="settingsMainHeader">
            <div>
              <p className="settingsSectionLabel">{tabsById.get(activeTab)?.description || 'Settings'}</p>
              <h2>{tabsById.get(activeTab)?.label || 'Settings'}</h2>
            </div>
          </div>

          {activeTab === 'general' ? (
            <section className="settingsCard">
              <div className="settingsGeneralSnapshot" aria-label="Organization overview">
                <div><span>Organization name</span><strong>{values.name || 'Not provided'}</strong></div>
                <div><span>Organization type</span><strong>{getTenantTypeLabel(values.type)}</strong></div>
                <div><span>SmartCampus subdomain</span><strong>{academySubdomain}</strong><a href={academySubdomainURL} target="_blank" rel="noreferrer">Open academy</a></div>
                <div><span>Country</span><strong>{currentCountry?.country || 'Not provided'}</strong></div>
                <div><span>Phone number</span><strong>{profile?.phone || user?.phoneNumber || 'Not provided'}</strong></div>
                <div><span>Account email</span><strong>{profile?.email || user?.email || 'Not provided'}</strong></div>
                <div><span>Account owner</span><strong>{profile?.displayName || user?.displayName || 'Not provided'}</strong></div>
                <div><span>Timezone</span><strong>{values.timezone || 'Not provided'}</strong></div>
                <div><span>Currency</span><strong>{values.currency || 'Not provided'}</strong></div>
              </div>
              <div className="settingsGeneralReadOnlyFooter">
                <div><p className="settingsSectionLabel">Academy details</p><h3>Everything looks right?</h3><p>These details are read-only here. Open Edit academy when you need to update your workspace.</p></div>
                <Button type="button" variant="secondary" icon={Settings} onClick={() => { setEditingAcademy(true); setActiveTab('edit academy'); }}>Edit academy</Button>
              </div>
            </section>
          ) : null}

          {activeTab === 'edit academy' ? (
            <section className="settingsCard">
              <div className="settingsInfoCard settingsGeneralEditIntro">
                <div><p className="settingsSectionLabel">Academy details</p><h3>Edit academy</h3><p>Update the details that shape your academy workspace and public experience. University is not available as a switch option.</p></div>
                <Button type="button" variant="secondary" onClick={() => { setEditingAcademy(false); setActiveTab('general'); }}>Back to general</Button>
              </div>
              <div className="settingsGeneralAccountDetails" aria-label="Registration details">
                <p className="settingsSectionLabel">Registration details</p>
                <div><span>Country</span><strong>{currentCountry?.country || 'Not provided'}</strong></div>
                <div><span>Phone number</span><strong>{profile?.phone || user?.phoneNumber || 'Not provided'}</strong></div>
                <div><span>Account email</span><strong>{profile?.email || user?.email || 'Not provided'}</strong></div>
                <small>These account details were captured during registration and are managed from your account profile.</small>
              </div>
              <form id="edit-academy-form" onSubmit={onSubmit} className="settings-form settingsFormGrid">
                <div className="settingsFormMain">
                  <FormField label="Country" id="settings-country">
                    <select
                      id="settings-country"
                      required
                      value={values.countryCode}
                      onChange={(event) => {
                        const country = getCountryDetails(event.target.value);
                        if (country) setValues((current) => ({ ...current, ...country }));
                      }}
                    >
                      {orderedPhoneCountries.map(([code, name, countryDialCode]) => <option key={`${code}-${countryDialCode}`} value={code}>{name} (+{countryDialCode})</option>)}
                    </select>
                  </FormField>
                  <FormField label="Phone number" id="settings-phone">
                    <div className="settingsPhoneInput">
                      <Phone size={16} aria-hidden="true" />
                      <span>+{currentCountry?.countryDialCode || values.countryDialCode}</span>
                      <input id="settings-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="712 345 678" required value={values.phone} onChange={(event) => setValues({ ...values, phone: event.target.value })} />
                    </div>
                  </FormField>
                  <div className="settingsGeneralDerivedSettings" aria-live="polite">
                    <div><span>Timezone</span><strong>{values.timezone}</strong><small>Updates automatically from country.</small></div>
                    <div><span>Currency</span><strong>{values.currency}</strong><small>Updates automatically from country.</small></div>
                  </div>
                  <FormField label="Organization Name" id="settings-name">
                    <input id="settings-name" required value={values.name} onChange={(event) => setValues({ ...values, name: event.target.value })} />
                  </FormField>
                  <div className="settingsGeneralSwitch">
                    <div><p className="settingsSectionLabel">Workspace classification</p><h3>Switch academy type</h3><p>Choose the type that best describes your academy. University is not available as a switch option.</p></div>
                    <FormField label="New academy type" id="settings-type">
                      <select id="settings-type" value={values.type === 'university' ? '' : values.type} onChange={(event) => { if (event.target.value) setValues({ ...values, type: event.target.value }); }}>
                        {values.type === 'university' ? <option value="">Keep University</option> : null}
                        {switchableTenantTypes.map((type) => <option key={type} value={type}>{getTenantTypeLabel(type)}</option>)}
                      </select>
                    </FormField>
                  </div>
                  <div className="settingsGeneralEditActions">
                    <Button type="submit" loading={saving} icon={Save}>
                      Save changes
                    </Button>
                  </div>
                </div>
              </form>
            </section>
          ) : null}

          {activeTab === 'fee-payment' ? (
            <section className="settingsCard">
              <div className="feePaymentHero">
                <div className="feePaymentHeroCopy"><p className="settingsSectionLabel">Payments setup</p><h3>Give students a simple way to pay.</h3><p>Connect one trusted provider, verify it with a real KES 5 prompt, and make it the default for your academy.</p></div>
                <div className="feePaymentHeroStatus"><span className={activePaymentMethods.length ? 'is-ready' : ''}>{activePaymentMethods.length ? 'Ready to collect' : 'Setup required'}</span><strong>{activePaymentMethods.length} active {activePaymentMethods.length === 1 ? 'method' : 'methods'}</strong><small>{values.feePayment.preferredMethod ? `Default: ${values.feePayment.preferredMethod === 'lotusPay' ? 'Lotus Pay' : values.feePayment.preferredMethod === 'paystack' ? 'Paystack' : 'M-Pesa Paybill'}` : 'Choose a default after setup'}</small></div>
              </div>
              <div className="feePaymentJourney" aria-label="Payment setup steps">
                <div className="feePaymentJourneyStep is-current"><span>01</span><div><strong>Choose</strong><small>Select a provider below</small></div></div><div className="feePaymentJourneyLine" /><div className="feePaymentJourneyStep"><span>02</span><div><strong>Configure</strong><small>Enter your provider details</small></div></div><div className="feePaymentJourneyLine" /><div className="feePaymentJourneyStep"><span>03</span><div><strong>Test</strong><small>Verify with a KES 5 prompt</small></div></div><div className="feePaymentJourneyLine" /><div className="feePaymentJourneyStep"><span>04</span><div><strong>Save</strong><small>Set your default method</small></div></div>
              </div>
              <div className="settingsInfoCard">
                <div><p className="settingsSectionLabel">Step 1 · Choose a provider</p><h3>Which payment method fits your academy?</h3><p>Set up one or more providers. The method you save successfully becomes the default automatically, and you can change the default at any time.</p></div>
                <div className="settingsFeePaymentSummary"><div className="settingsBrandPreview settingsFeePaymentPreview"><CreditCard size={18} /><strong>{activePaymentMethods.length ? `Active: ${activePaymentMethods.join(', ')}` : 'No payment method active'}</strong></div><label className="settingsPreferredPayment"><span>Preferred payment method</span><select value={values.feePayment.preferredMethod || ''} onChange={(event) => updateFeePayment('preferredMethod', event.target.value)}><option value="">Choose a method</option><option value="lotusPay">Lotus Pay</option><option value="paystack">Paystack</option><option value="paybill">M-Pesa Paybill</option></select><Button type="button" disabled={!values.feePayment.preferredMethod} loading={saving} onClick={() => saveFeePaymentSettings('Preferred payment method')}>Save preference</Button></label></div>
              </div>
              <div className="settingsProcessorTabs" role="tablist" aria-label="Fee payment setup">
                {[['lotusPay', 'Lotus Pay'], ['paystack', 'Paystack'], ['paybill', 'M-Pesa Paybill Number']].map(([id, label]) => <button key={id} type="button" className={processorTab === id ? 'is-active' : ''} onClick={() => setProcessorTab(id)} role="tab" aria-selected={processorTab === id}>{label}</button>)}
              </div>
              <div className={`settingsProcessorPanel ${processorTab === 'paybill' && paybillCredentialsSaved ? 'is-paybill-configured' : ''}`}>
                <div className="feePaymentPanelKicker"><span>Step 2</span><div><strong>{processorTab === 'lotusPay' ? 'Connect Lotus Pay' : processorTab === 'paystack' ? 'Connect Paystack' : 'Connect M-Pesa Paybill'}</strong><small>Enter the credentials supplied by your payment provider, then test the connection before saving.</small></div></div>
                {processorTab === 'paybill' && paybillCredentialsSaved ? <div className="paybillConfiguredCard"><div className="paybillConfiguredCopy"><CheckCircle2 size={20} /><span><strong>M-Pesa Paybill is configured successfully.</strong><small>Your Paybill credentials are securely saved. Run a KES 5 test payment whenever you want to confirm the connection still works.</small></span></div><div className="paybillConfiguredActions"><Button type="button" variant="secondary" loading={paymentTest.status === 'testing' && paymentTest.tab === 'paybill'} onClick={() => testPaymentMethod('paybill', 'M-Pesa Paybill')} icon={Smartphone}>Test Paybill payment</Button><Button type="button" variant="secondary" onClick={() => { setPaybillCredentialsSaved(false); setPaymentTest({ tab: 'paybill', fingerprint: '', status: 'idle', error: '' }); }}>Change configuration</Button></div></div> : null}
                {processorTab === 'paybill' ? <><div className="settingsMpesaIntro"><h4>Collect fees directly through your M-Pesa Paybill.</h4><p>Students enter their M-Pesa number, receive a payment prompt on their phone, and confirm the payment securely. After payment, the transaction is recorded automatically on your academy platform and the student receives a receipt by email, SMS, and WhatsApp.</p></div><div className="settingsLotusSteps"><div><b>1</b><span><strong>Get an M-Pesa Paybill number</strong><small>Request your Paybill from Safaricom.</small></span></div><div><b>2</b><span><strong>Set up your Safaricom organisation account</strong><small>Ask Safaricom to create the account and provide your organisation username.</small></span></div><div><b>3</b><span><strong>Create your Daraja account and go live</strong><small>Register your Paybill application on Daraja and click Go Live.</small></span></div><div><b>4</b><span><strong>Paste your credentials and test</strong><small>After approval, Safaricom emails your passkey, consumer key, and consumer secret.</small></span></div></div><FormField label="M-Pesa Paybill number" id="fee-paybill-number"><input id="fee-paybill-number" value={values.feePayment.lotusPay.paybill} onChange={(event) => updateProcessor('lotusPay', 'paybill', event.target.value)} placeholder="Enter your M-Pesa paybill number" /></FormField><div className="settingsGrid2"><FormField label="M-Pesa passkey" id="fee-paybill-passkey"><input id="fee-paybill-passkey" type="password" value={values.feePayment.lotusPay.mpesaPasskey} onChange={(event) => updateProcessor('lotusPay', 'mpesaPasskey', event.target.value)} placeholder="Enter passkey" /></FormField><FormField label="Consumer key" id="fee-paybill-consumer-key"><input id="fee-paybill-consumer-key" value={values.feePayment.lotusPay.mpesaConsumerKey} onChange={(event) => updateProcessor('lotusPay', 'mpesaConsumerKey', event.target.value)} placeholder="Enter consumer key" /></FormField><FormField label="Consumer secret" id="fee-paybill-consumer-secret"><input id="fee-paybill-consumer-secret" type="password" value={values.feePayment.lotusPay.mpesaConsumerSecret} onChange={(event) => updateProcessor('lotusPay', 'mpesaConsumerSecret', event.target.value)} placeholder="Enter consumer secret" /></FormField></div><p className="settingsMpesaDarajaLink">Set up your integration in <a href="https://developer.safaricom.co.ke/" target="_blank" rel="noreferrer">Daraja <ExternalLink size={13} /></a>.</p><div className="settingsProcessorSave"><Button type="button" variant="secondary" loading={paymentTest.status === 'testing' && paymentTest.tab === 'paybill'} onClick={() => testPaymentMethod('paybill', 'M-Pesa Paybill')} icon={CheckCircle2}>Test connection</Button><Button type="button" disabled={!paymentTestPassed('paybill')} loading={saving} icon={Save} onClick={() => saveFeePaymentSettings('M-Pesa Paybill')}>Save paybill number</Button></div>{paymentTestPassed('paybill') ? <p className="settingsPaymentTestSuccess">Connection tested successfully. You can save this method.</p> : null}</> : null}
                {processorTab === 'lotusPay' ? <div className="settingsLotusContent">
                  <div className="settingsProcessorPanelHeader"><div><strong>Lotus Pay</strong><small>Simple local fee collection with M-Pesa</small></div></div>
                  <div className="settingsLotusIntro">
                    <h4>Let students pay in seconds.</h4>
                    <p>Lotus Pay is a payment aggregator connected with Choice Bank that helps your academy collect fees digitally. When a student chooses to pay, they enter their M-Pesa number, receive a secure payment prompt on their phone, and confirm the payment directly from M-Pesa.</p>
                    <p>Once the payment is completed, the collection is handled through Lotus Pay and can settle into your configured account. The payment is recorded automatically on your academy platform, and the student receives a payment confirmation and receipt by email, SMS, and WhatsApp. You can sign in to your Lotus Pay account to view collections and manage disbursements to supported channels such as bank and mobile money.</p>
                  </div>
                  <div className="settingsProviderTimeline"><Clock3 size={14} /> Typical verification time: <strong>up to 72 working hours</strong></div>
                  <div className="settingsLotusSteps">
                    <div><b>1</b><span><strong>Create a Lotus Pay account</strong><small>Register your academy on Lotus Pay.</small></span></div>
                    <div><b>2</b><span><strong>Ask Lotus Pay to enable your API key</strong><small>Request API access from your Lotus Pay account.</small></span></div>
                    <div><b>3</b><span><strong>Paste the key below and save</strong><small>We will use it to connect student fee payments.</small></span></div>
                  </div>
                  <a className="settingsLotusLink" href="https://www.withlotus.com/" target="_blank" rel="noreferrer">Create or open your Lotus Pay account <ExternalLink size={14} /></a>
                  {lotusPayCredentialsSaved && !editingLotusPayCredentials ? <div className="settingsPaystackConfigured"><div><CheckCircle2 size={17} /><span><strong>Lotus Pay is configured successfully.</strong><small>Your API key is encrypted and stored securely. Students can now pay through Lotus Pay.</small></span></div><Button type="button" variant="secondary" loading={paymentTest.status === 'testing' && paymentTest.tab === 'lotusPay'} onClick={openLotusPayTestModal} icon={Smartphone}>Test with M-Pesa</Button><Button type="button" variant="secondary" onClick={() => { setEditingLotusPayCredentials(true); setPaymentTest({ tab: 'lotusPay', fingerprint: '', status: 'idle', error: '' }); }}>Change API key</Button></div> : <><FormField label="Lotus Pay API key" id="lotus-api-key"><input id="lotus-api-key" type="password" value={values.feePayment.lotusPay.apiKey} onChange={(event) => updateProcessor('lotusPay', 'apiKey', event.target.value)} placeholder="Paste your Lotus Pay API key" /></FormField><p className="settingsSecretNote">Keep your API key private. It is encrypted on the server and never shown on public pages.</p><div className="settingsProcessorSave"><Button type="button" variant="secondary" loading={paymentTest.status === 'testing' && paymentTest.tab === 'lotusPay'} onClick={openLotusPayTestModal} icon={Smartphone}>Test with M-Pesa</Button><Button type="button" disabled={!paymentTestPassed('lotusPay')} loading={saving} icon={Save} onClick={() => saveFeePaymentSettings('Lotus Pay')}>Save Lotus Pay</Button></div></>}
                  {paymentTest.tab === 'lotusPay' && paymentTest.status === 'error' ? <p className="settingsPaymentTestError">{paymentTest.error}</p> : null}{paymentTestPassed('lotusPay') ? <p className="settingsPaymentTestSuccess">Payment verified successfully. You can save this method.</p> : null}
                </div> : null}
                {processorTab === 'paystack' ? <><div className="settingsProcessorPanelHeader"><div><strong>Paystack</strong><small>M-Pesa, PesaLink, cards and Airtel Money</small></div><label className="settingsProcessorToggle"><input type="checkbox" checked={values.feePayment.paystack.enabled} onChange={(event) => updateProcessor('paystack', 'enabled', event.target.checked)} /><span>{values.feePayment.paystack.enabled ? 'Enabled' : 'Off'}</span></label></div><div className="settingsPaystackIntro"><h4>Give students more ways to pay.</h4><p>Paystack lets your academy collect student fees online through supported local payment options such as M-Pesa, PesaLink, cards, and Airtel Money. Students choose their preferred option, complete the payment securely, and receive confirmation after the transaction.</p><p>After a successful payment, the payment is recorded automatically on your academy platform and the student receives a receipt by email, SMS, and WhatsApp.</p><p>Enter your live keys from the Paystack dashboard, test the connection, then save the setup. Keep your secret key private.</p><p>Use <strong>Open test checkout</strong> to confirm that your live public key opens Paystack’s browser checkout. It opens a KES 10 test transaction; do not complete it unless you intend to make a real live payment.</p><div className="settingsLotusSteps"><div><b>1</b><span><strong>Create your Paystack account</strong><small>Register your academy and complete the account setup.</small></span></div><div><b>2</b><span><strong>Submit your KYC and go live</strong><small>Complete Paystack’s verification process and request live access.</small></span></div><div><b>3</b><span><strong>Paste the approved credentials here</strong><small>After approval, copy the keys from your Paystack dashboard, test the connection, and save.</small></span></div></div><a className="settingsLotusLink" href="https://paystack.com/" target="_blank" rel="noreferrer">Create or open your Paystack account <ExternalLink size={14} /></a></div><>{paystackCredentialsSaved && !editingPaystackCredentials ? null : <div className="settingsGrid2"><FormField label="Live public key" id="paystack-public-key"><input id="paystack-public-key" value={values.feePayment.paystack.publicKey} onChange={(event) => updateProcessor('paystack', 'publicKey', event.target.value)} placeholder="pk_live_..." /></FormField><FormField label="Live secret key" id="paystack-secret-key"><input id="paystack-secret-key" type="password" value={values.feePayment.paystack.secretKey} onChange={(event) => updateProcessor('paystack', 'secretKey', event.target.value)} placeholder="sk_live_..." /></FormField></div>}</><div className="settingsProcessorSave">{paystackCredentialsSaved && !editingPaystackCredentials ? <div className="settingsPaystackConfigured"><div><CheckCircle2 size={17} /><span><strong>Paystack is configured successfully.</strong><small>Your live credentials are securely saved. Run a small test payment to confirm the complete checkout flow.</small></span></div><Button type="button" variant="secondary" loading={paymentTest.status === 'testing' && paymentTest.tab === 'paystack'} onClick={() => testPaymentMethod('paystack', 'Paystack')} icon={CheckCircle2}>Test Paystack payment</Button><Button type="button" variant="secondary" onClick={() => { setEditingPaystackCredentials(true); setPaymentTest({ tab: 'paystack', fingerprint: '', status: 'idle', error: '' }); }}>Change Paystack credentials</Button></div> : <><Button type="button" variant="secondary" loading={paymentTest.status === 'testing' && paymentTest.tab === 'paystack'} onClick={() => testPaymentMethod('paystack', 'Paystack')} icon={CheckCircle2}>Open test checkout</Button>{editingPaystackCredentials ? <Button type="button" disabled={!paymentTestPassed('paystack')} loading={saving} icon={Save} onClick={() => saveFeePaymentSettings('Paystack')}>Save Paystack</Button> : <Button type="button" variant="secondary" onClick={() => { setEditingPaystackCredentials(true); setPaymentTest({ tab: 'paystack', fingerprint: '', status: 'idle', error: '' }); }}>Change Paystack credentials</Button>}</>}</div>{paymentTest.tab === 'paystack' && paymentTest.status === 'error' ? <p className="settingsPaymentTestError">{paymentTest.error}</p> : null}{paymentTestPassed('paystack') ? <p className="settingsPaymentTestSuccess">Checkout opened successfully. You can save or retest this method.</p> : null}</> : null}
              </div>
              <form id="fee-payment-form" onSubmit={onSubmit} className="settings-form settingsFormGrid">
                <div className="settingsFormMain settingsFeeLegacyFields">
                  <div className="settingsSectionLabel">Accepted payment methods</div>
                  <div className="settingsFeeMethods">
                    {[['cash', 'Cash / in person'], ['bank_transfer', 'Bank transfer'], ['mobile_money', 'Mobile money'], ['card', 'Card payment'], ['online', 'Online payment link']].map(([id, label]) => <label key={id}><input type="checkbox" checked={values.feePayment.methods.includes(id)} onChange={(event) => setValues({ ...values, feePayment: { ...values.feePayment, methods: event.target.checked ? [...values.feePayment.methods, id] : values.feePayment.methods.filter((method) => method !== id) } })} /> <span>{label}</span></label>)}
                  </div>
                  {values.feePayment.methods.includes('bank_transfer') ? <div className="settingsGrid2"><FormField label="Bank name" id="fee-bank-name"><input id="fee-bank-name" value={values.feePayment.bankName} onChange={(event) => setValues({ ...values, feePayment: { ...values.feePayment, bankName: event.target.value } })} placeholder="e.g. KCB Bank" /></FormField><FormField label="Account name" id="fee-account-name"><input id="fee-account-name" value={values.feePayment.accountName} onChange={(event) => setValues({ ...values, feePayment: { ...values.feePayment, accountName: event.target.value } })} placeholder="Academy account name" /></FormField><FormField label="Account number" id="fee-account-number"><input id="fee-account-number" value={values.feePayment.accountNumber} onChange={(event) => setValues({ ...values, feePayment: { ...values.feePayment, accountNumber: event.target.value } })} placeholder="Account number" /></FormField><FormField label="Branch (optional)" id="fee-bank-branch"><input id="fee-bank-branch" value={values.feePayment.branch} onChange={(event) => setValues({ ...values, feePayment: { ...values.feePayment, branch: event.target.value } })} placeholder="Branch" /></FormField></div> : null}
                  {values.feePayment.methods.includes('mobile_money') ? <div className="settingsGrid2"><FormField label="Mobile money provider" id="fee-mobile-provider"><input id="fee-mobile-provider" value={values.feePayment.mobileMoneyProvider} onChange={(event) => setValues({ ...values, feePayment: { ...values.feePayment, mobileMoneyProvider: event.target.value } })} placeholder="e.g. M-Pesa" /></FormField><FormField label="Paybill / phone number" id="fee-mobile-number"><input id="fee-mobile-number" value={values.feePayment.mobileMoneyNumber} onChange={(event) => setValues({ ...values, feePayment: { ...values.feePayment, mobileMoneyNumber: event.target.value } })} placeholder="Payment number" /></FormField></div> : null}
                  {values.feePayment.methods.includes('online') ? <FormField label="Online payment link" id="fee-online-link"><input id="fee-online-link" type="url" value={values.feePayment.onlinePaymentLink} onChange={(event) => setValues({ ...values, feePayment: { ...values.feePayment, onlinePaymentLink: event.target.value } })} placeholder="https://payments.example.com/..." /></FormField> : null}
                  <FormField label="Instructions for students" id="fee-payment-instructions"><textarea id="fee-payment-instructions" rows="5" value={values.feePayment.paymentInstructions} onChange={(event) => updateFeePayment('paymentInstructions', event.target.value)} placeholder="Include the account reference students should use and what proof of payment to send." /></FormField>
                  <label className="settingsFeeProof"><input type="checkbox" checked={values.feePayment.requireProof} onChange={(event) => updateFeePayment('requireProof', event.target.checked)} /> <span><strong>Ask students to submit proof of payment</strong><small>Useful when payments are made by bank transfer or mobile money.</small></span></label>
                </div>
              </form>
            </section>
          ) : null}

          {activeTab === 'branding' ? (
            <section className="settingsCard">
              <div className="settingsInfoCard">
                <div>
                  <p className="settingsSectionLabel">Brand system</p>
                  <h3>Visual identity</h3>
                  <p>Set the primary color used in the academy app, forms and student-facing screens.</p>
                </div>
                <div className="settingsBrandPreview" style={{ background: brandPalette.primary }}>
                  <CheckCircle2 size={18} />
                  <strong>{brandPalette.primary}</strong>
                </div>
              </div>
              <div className="settingsBrandField settingsBrandField--theme">
                <div className="settingsColorPickerShell">
                  <label htmlFor="primary-color-picker">
                    <span>Color picker</span>
                    <strong>Open the popup to choose visually</strong>
                  </label>
                  <input
                    id="primary-color-picker"
                    type="color"
                    value={brandPalette.primary}
                    onChange={(event) => setValues({ ...values, primaryColor: event.target.value })}
                    aria-label="Primary color picker"
                  />
                </div>

                <FormField label="Primary Color Code" id="primary-color">
                  <input
                    id="primary-color"
                    value={values.primaryColor}
                    onChange={(event) => setValues({ ...values, primaryColor: event.target.value })}
                    placeholder="#0f766e"
                  />
                </FormField>

                <div className="settingsColorPresets" aria-label="Color presets">
                  {colorPresets.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      className={brandPalette.primary === getThemePalette(preset).primary ? 'is-active' : ''}
                      onClick={() => setValues({ ...values, primaryColor: preset })}
                      style={{ background: preset }}
                      aria-label={`Use ${preset} as the primary color`}
                    />
                  ))}
                </div>

                <p className="muted">Pick a color visually or paste a color code. The saved value will drive the dashboard, menu theme, and public academy templates.</p>
              </div>

              <div className="settingsBrandActions">
                <Button
                  type="button"
                  loading={saving}
                  icon={Save}
                  onClick={() => persistSettings({ ...values, primaryColor: brandPalette.primary }, 'Branding updated.')}
                >
                  Save branding
                </Button>
              </div>

              <div className="settingsWebsiteSection">
                <div className="settingsTemplateHeader">
                  <div>
                    <p className="settingsSectionLabel">Public website content</p>
                    <h3>Edit your homepage content</h3>
                    <p>Change the hero text, intro text and hero image that appear on the public academy page.</p>
                  </div>
                  <div className="settingsTemplateCurrent">
                    <span>Hero image</span>
                    <strong>{values.publicHeroImageURL ? 'Custom image set' : 'Using default image'}</strong>
                  </div>
                </div>

                <div className="settingsWebsiteGrid">
                  <div className="settingsWebsiteForm">
                    <FormField label="Hero Title" id="public-hero-title">
                      <input
                        id="public-hero-title"
                        value={values.publicHeroTitle}
                        onChange={(event) => setValues({ ...values, publicHeroTitle: event.target.value })}
                        placeholder="Build your future at ..."
                      />
                    </FormField>
                    <FormField label="Hero Description" id="public-hero-description">
                      <textarea
                        id="public-hero-description"
                        rows={4}
                        value={values.publicHeroDescription}
                        onChange={(event) => setValues({ ...values, publicHeroDescription: event.target.value })}
                        placeholder="A short intro that explains the academy and invites students to apply."
                      />
                    </FormField>
                    <FormField label="Hero Image URL" id="public-hero-image">
                      <input
                        id="public-hero-image"
                        value={values.publicHeroImageURL}
                        onChange={(event) => setValues({ ...values, publicHeroImageURL: event.target.value })}
                        placeholder="https://..."
                      />
                    </FormField>
                    <div className="settingsGrid2">
                      <FormField label="About Heading" id="public-about-title">
                        <input
                          id="public-about-title"
                          value={values.publicAboutTitle}
                          onChange={(event) => setValues({ ...values, publicAboutTitle: event.target.value })}
                          placeholder="About us"
                        />
                      </FormField>
                      <FormField label="About Description" id="public-about-description">
                        <input
                          id="public-about-description"
                          value={values.publicAboutDescription}
                          onChange={(event) => setValues({ ...values, publicAboutDescription: event.target.value })}
                          placeholder="A short summary for the about section"
                        />
                      </FormField>
                    </div>
                    <div className="settingsBrandActions">
                      <Button
                        type="button"
                        loading={saving}
                        icon={Save}
                        onClick={() => persistSettings({ ...values, primaryColor: brandPalette.primary }, 'Website content updated.')}
                      >
                        Save content
                      </Button>
                    </div>
                  </div>

                  <div className="settingsWebsitePreview">
                    <span className="settingsPreviewLabel">Website preview</span>
                    <strong>{values.publicHeroTitle || activeTenant?.name || 'Your academy'}</strong>
                    <p>{values.publicHeroDescription || 'Your hero copy will appear here.'}</p>
                    <div
                      className="settingsWebsitePreviewImage"
                      style={{
                        backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.18), rgba(15, 118, 110, 0.42)), url(${values.publicHeroImageURL || academyHeroImage})`,
                      }}
                    />
                    <small>{values.publicHeroImageURL || 'Default academy hero image will be used until you add one.'}</small>
                  </div>
                </div>
              </div>

              <div className="settingsTemplateSection">
                <div className="settingsTemplateHeader">
                  <div>
                    <p className="settingsSectionLabel">Public website</p>
                    <h3>Choose a template</h3>
                    <p>Pick a front-facing academy look, then preview it before publishing to students.</p>
                  </div>
                  <div className="settingsTemplateCurrent">
                    <span>Active template</span>
                    <strong>{publicTemplates.find((template) => template.id === values.publicTemplate)?.name || 'Modern Admissions'}</strong>
                  </div>
                </div>

                <div className="settingsTemplateGrid">
                  {publicTemplates.map((template) => {
                    const isActive = values.publicTemplate === template.id;
                    return (
                      <article key={template.id} className={`settingsTemplateCard ${isActive ? 'is-active' : ''}`}>
                        <div className="settingsTemplateCardTop">
                          <div>
                            <span>{template.name}</span>
                            <strong>{template.tone}</strong>
                          </div>
                          {isActive ? <CheckCircle2 size={18} /> : null}
                        </div>
                        <p>{template.description}</p>
                        <div className="settingsTemplateActions">
                          <Button
                            type="button"
                            variant={isActive ? 'secondary' : 'secondary'}
                            icon={ExternalLink}
                            onClick={() => previewTemplate(template.id)}
                          >
                            Preview
                          </Button>
                          <Button
                            type="button"
                            icon={CheckCircle2}
                            loading={saving && isActive}
                            onClick={() => savePublicTemplate(template.id)}
                          >
                            {isActive ? 'Selected' : 'Use template'}
                          </Button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            </section>
          ) : null}

          {activeTab === 'domains' ? (
            <section className="settingsCard">
              <div className="settingsDomainGrid">
                <div className="settingsDomainCard">
                  <span>Full subdomain</span>
                  <strong>{academySubdomain}</strong>
                  <p className="muted">Students can use this exact address to open the academy.</p>
                  <Button variant="secondary" icon={Copy} onClick={copyAcademySubdomain}>Copy subdomain</Button>
                </div>
                <div className="settingsDomainCard">
                  <span>Custom domain</span>
                  <strong>{activeTenant?.domain?.customDomain || 'Not connected'}</strong>
                  <p className="muted">You must be subscribed to the Pro package to use this feature.</p>
                  <Button variant="secondary" onClick={() => notify('You must be subscribed to the Pro package to use this feature.')}>Pro package required</Button>
                </div>
              </div>
            </section>
          ) : null}

          {activeTab === 'members & roles' ? (
            <section className="settingsCard">
              <div className="settingsMembersHeader">
                <div><p className="settingsSectionLabel">Workspace access</p><h3>Members and roles</h3><p>See who can access this academy and assign team roles from your employee directory.</p></div>
                <Button icon={UserPlus} onClick={openTeamModal} disabled={!availableEmployees.length}>Add from employees</Button>
              </div>
              {teamLoading ? <LoadingState label="Loading members and roles..." /> : (
                <>
                  <div className="settingsMembersTable">
                    <DataTable
                      rows={teamRows}
                      columns={[
                        { key: 'displayName', label: 'Member', render: (member) => <div className="settingsMemberName"><strong>{member.displayName || 'Unnamed member'}</strong>{member.isCurrentUser ? <span>Signed in</span> : member.isInvitation ? <span>Invitation pending</span> : null}</div> },
                        { key: 'email', label: 'Email', render: (member) => member.email || 'Not provided' },
                        { key: 'roleId', label: 'Role', render: (member) => teamRoleName(member.roleId) },
                        { key: 'status', label: 'Status', render: (member) => <StatusBadge status={member.status} /> },
                      ]}
                      empty={<EmptyState title="No members yet." message="The signed-in account will appear here after the academy loads." icon={UsersRound} />}
                    />
                  </div>
                  <div className="settingsRolesDirectory">
                    <div><p className="settingsSectionLabel">Role directory</p><h4>Available roles</h4><p>Choose one of these roles when adding an employee to academy access.</p></div>
                    <div className="settingsRolesList">{teamRoles.filter((item) => item.id !== 'student').map((item) => <div key={item.id}><strong>{item.name}</strong><span>{item.description || 'Academy access role'}</span></div>)}</div>
                  </div>
                  {!availableEmployees.length ? <p className="settingsMembersHint">All employees with email addresses are already members or have a pending invitation. Add an employee in the Employees section to invite someone new.</p> : null}
                </>
              )}
            </section>
          ) : null}

          {activeTab === 'billing' ? (
            <section className="settingsBilling">
              <nav className="settingsBillingPlanTabs" aria-label="Billing plans">
                {[
                  { id: 'free', label: 'Free Tier', note: 'Start free' },
                  { id: 'starter', label: 'Starter', note: 'Up to 20 learners' },
                  { id: 'growth', label: 'Growth', note: 'Up to 150 learners' },
                  { id: 'pro', label: 'Pro', note: 'Full operations' },
                ].map((plan) => (
                  <button key={plan.id} type="button" className={billingPlan === plan.id ? 'active' : ''} onClick={() => setBillingPlan(plan.id)} aria-pressed={billingPlan === plan.id}>
                    <strong>{plan.label}</strong>
                    <small>{plan.note}</small>
                  </button>
                ))}
              </nav>

                <div className="settingsBillingPlanView">
                  {billingPlan === 'free' ? (
                    <><div className="settingsBillingPlanHero settingsBillingPlanHero--free"><p className="settingsSectionLabel">Your starting point</p><h3>Everything essential, <em>always free.</em></h3><p>Run your private teaching business with the tools you need to organise learners, courses, applications, classes, fees and communication at no monthly cost. The Free Tier is optimized for up to 3 students.</p><span className="settingsBillingPlanStatus">Optimized for up to 3 students</span></div><FeatureAccessList /></>
                  ) : billingPlan === 'starter' ? (
                    <><div className="settingsBillingPlanHero"><p className="settingsSectionLabel">Private instructor · Starter</p><h3>More room to teach.<br /><em>Built for 20 learners.</em></h3><div className="settingsBillingPlanPrice">KSh 950 <small>/ month</small></div><p>Everything in Free, with more learner capacity, longer virtual classes and a larger communication and payment allowance.</p><span className="settingsBillingPlanStatus">Recommended for up to 20 learners</span></div><FeatureAccessList tierLabel="Private instructor · Starter" heading="Everything in Free, with more room" intro="Keep the full Free Tier foundation and unlock a practical allowance for a growing private teaching business." features={starterTierFeatures} /></>
                  ) : billingPlan === 'growth' ? (
                    <><div className="settingsBillingPlanHero"><p className="settingsSectionLabel">Private instructor · Growth</p><h3>Grow your reach.<br /><em>Built for 150 learners.</em></h3><div className="settingsBillingPlanPrice">KSh 4,950 <small>/ month</small></div><p>Everything in Starter, with room for a larger learner community and WhatsApp included across all notifications.</p><span className="settingsBillingPlanStatus">Recommended for up to 150 learners</span></div><FeatureAccessList tierLabel="Private instructor · Growth" heading="Everything in Starter, with full communication" intro="Keep your teaching, payment and learner workflows connected while reaching a larger community through email, SMS and WhatsApp." features={growthTierFeatures} /></>
                  ) : billingPlan === 'pro' ? (
                    <><div className="settingsBillingPlanHero"><p className="settingsSectionLabel">Private instructor · Pro</p><h3>The complete workspace.<br /><em>Built for 1,000 learners.</em></h3><div className="settingsBillingPlanPrice">KSh 20,000 <small>/ month</small></div><p>Everything in Growth, with the capacity and advanced tools to operate a serious learning business at scale.</p><span className="settingsBillingPlanStatus">Recommended for up to 1,000 learners</span></div><FeatureAccessList tierLabel="Private instructor · Pro" heading="The complete SmartCampus workspace" intro="Unlock the full learning, finance, communication, insight and team experience for a large private teaching business." features={proTierFeatures} /></>
                  ) : (
                    <div className="settingsBillingPlanHero"><p className="settingsSectionLabel">{billingPlan === 'starter' ? 'A simple step up' : billingPlan === 'growth' ? 'More room to grow' : 'The complete workspace'}</p><h3>{billingPlan === 'starter' ? 'Starter for focused academies.' : billingPlan === 'growth' ? 'Growth for growing teams.' : 'Pro for full operations.'}</h3><div className="settingsBillingPlanPrice">{billingPlan === 'starter' ? 'KSh 950' : billingPlan === 'growth' ? 'KSh 5,000' : 'KSh 20,000'} <small>/ month</small></div><p>{billingPlan === 'starter' ? 'Bring more learners, storage and communication into one predictable workspace.' : billingPlan === 'growth' ? 'Give your team more capacity, automation and insight as your academy expands.' : 'Unlock the complete SmartCampus experience for larger, more demanding operations.'}</p><button type="button" className="settingsBillingPlanAction" onClick={() => notify(`${billingPlan[0].toUpperCase()}${billingPlan.slice(1)} plan selection will be available when billing is connected.`)}>Choose {billingPlan[0].toUpperCase()} {billingPlan.slice(1)}</button></div>
                  )}
                </div>
            </section>
          ) : null}

          <Modal open={teamModalOpen} onClose={() => setTeamModalOpen(false)} title="Add member from employees" description="Choose an employee and the role they should use when they join this academy.">
            <form className="modal-form" onSubmit={addEmployeeMember}>
              {availableEmployees.length ? (
                <>
                  <FormField label="Employee" id="settings-member-employee">
                    <select id="settings-member-employee" required value={teamValues.employeeId} onChange={(event) => setTeamValues({ ...teamValues, employeeId: event.target.value })}>
                      {availableEmployees.map((employee) => <option key={employee.id} value={employee.id}>{employee.fullName || 'Unnamed employee'} · {employee.email}</option>)}
                    </select>
                  </FormField>
                  <FormField label="Role" id="settings-member-role">
                    <select id="settings-member-role" required value={teamValues.roleId} onChange={(event) => setTeamValues({ ...teamValues, roleId: event.target.value })}>
                      {assignableRoles.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </select>
                  </FormField>
                  <div className="settingsMemberInviteNote"><Plus size={14} /><span>The employee will appear as pending until they accept the invitation.</span></div>
                </>
              ) : <EmptyState title="No employees available." message="Add an employee with an email address first, or check that they do not already have access." icon={UsersRound} />}
              <div className="form-actions"><Button type="button" variant="ghost" onClick={() => setTeamModalOpen(false)}>Cancel</Button><Button type="submit" icon={UserPlus} loading={teamSaving} disabled={!availableEmployees.length}>Add member</Button></div>
            </form>
          </Modal>
        </section>
      </section>
      <Modal
        open={lotusPayTestModalOpen}
        onClose={() => { if (!['sending', 'confirming'].includes(lotusPayTestStage)) setLotusPayTestModalOpen(false); }}
        title="Test Lotus Pay with M-Pesa"
        description={lotusPayTestStage === 'confirming' ? 'Waiting for Lotus Pay to confirm your M-Pesa payment.' : 'We’ll send a real KES 5 payment prompt to the number below.'}
        className="lotus-pay-test-modal"
        closeDisabled={['sending', 'confirming'].includes(lotusPayTestStage)}
      >
        <form className="lotus-pay-test-form" onSubmit={submitLotusPayTest}>
          <div className="lotus-pay-test-banner">
            <span className="lotus-pay-test-icon"><Smartphone size={22} /></span>
            <div><strong>M-Pesa payment test</strong><p>Use a phone you can access now. Approve the prompt on your phone to verify the connection.</p></div>
          </div>
          <div className="lotus-pay-test-amount"><span>Test amount</span><strong>KES 5</strong><small>This test is not added to student fee records.</small></div>
          <FormField label="M-Pesa phone number" id="lotus-test-phone"><input id="lotus-test-phone" type="tel" inputMode="tel" autoComplete="tel" required disabled={lotusPayTestStage !== 'idle'} value={lotusPayTestPhone} onChange={(event) => setLotusPayTestPhone(event.target.value)} placeholder="e.g. 0712 345 678" /></FormField>
          {lotusPayTestStage === 'confirming' ? <div className="lotus-pay-test-status is-confirming" role="status"><span className="lotus-pay-test-spinner" /><div><strong>Confirming your payment…</strong><p>Approve the M-Pesa prompt on your phone. We’ll keep checking Lotus Pay automatically.</p></div></div> : null}
          {lotusPayTestStage === 'sending' ? <div className="lotus-pay-test-status is-confirming" role="status"><span className="lotus-pay-test-spinner" /><div><strong>Sending payment prompt…</strong><p>Connecting securely to Lotus Pay.</p></div></div> : null}
          {lotusPayTestStage === 'success' ? <div className="lotus-pay-test-status is-success" role="status"><CheckCircle2 size={20} /><div><strong>Payment confirmed successfully</strong><p>Lotus Pay is ready. You can now save this connection.</p></div></div> : null}
          {lotusPayTestStage === 'error' ? <div className="lotus-pay-test-status is-error" role="alert"><div><strong>We couldn’t confirm the payment</strong><p>{paymentTest.error}</p></div></div> : null}
          {lotusPayTestStage === 'idle' ? <p className="lotus-pay-test-help">Enter the number exactly as registered with M-Pesa. You’ll receive the STK prompt after selecting <strong>Send payment prompt</strong>.</p> : null}
          <div className="form-actions">{lotusPayTestStage === 'idle' ? <><Button type="button" variant="ghost" onClick={() => setLotusPayTestModalOpen(false)}>Cancel</Button><Button type="submit" icon={Smartphone}>Send payment prompt</Button></> : null}{['success', 'error'].includes(lotusPayTestStage) ? <Button type="button" onClick={() => setLotusPayTestModalOpen(false)}>Close</Button> : null}</div>
        </form>
      </Modal>
      <Modal
        open={paybillTestModalOpen}
        onClose={() => { if (!['sending', 'confirming'].includes(paybillTestStage)) setPaybillTestModalOpen(false); }}
        title="Test M-Pesa Paybill"
        description={paybillTestStage === 'confirming' ? 'Waiting for Safaricom to confirm your M-Pesa payment.' : 'We’ll send a real KES 5 payment prompt to the number below.'}
        className="lotus-pay-test-modal"
        closeDisabled={['sending', 'confirming'].includes(paybillTestStage)}
      >
        <form className="lotus-pay-test-form" onSubmit={submitPaybillTest}>
          <div className="lotus-pay-test-banner"><span className="lotus-pay-test-icon"><Smartphone size={22} /></span><div><strong>M-Pesa Paybill payment test</strong><p>Use a phone you can access now. Approve the prompt to verify your Paybill connection.</p></div></div>
          <div className="lotus-pay-test-amount"><span>Test amount</span><strong>KES 5</strong><small>This test is not added to student fee records.</small></div>
          <FormField label="M-Pesa phone number" id="paybill-test-phone"><input id="paybill-test-phone" type="tel" inputMode="tel" autoComplete="tel" required disabled={paybillTestStage !== 'idle'} value={paybillTestPhone} onChange={(event) => setPaybillTestPhone(event.target.value)} placeholder="e.g. 0712 345 678" /></FormField>
          {paybillTestStage === 'confirming' ? <div className="lotus-pay-test-status is-confirming" role="status"><span className="lotus-pay-test-spinner" /><div><strong>Confirming your payment…</strong><p>Approve the M-Pesa prompt on your phone. We’ll keep checking Safaricom automatically.</p></div></div> : null}
          {paybillTestStage === 'sending' ? <div className="lotus-pay-test-status is-confirming" role="status"><span className="lotus-pay-test-spinner" /><div><strong>Sending payment prompt…</strong><p>Connecting securely to Safaricom.</p></div></div> : null}
          {paybillTestStage === 'success' ? <div className="lotus-pay-test-status is-success" role="status"><CheckCircle2 size={20} /><div><strong>Payment confirmed successfully</strong><p>Your M-Pesa Paybill is ready. You can now save this connection.</p></div></div> : null}
          {paybillTestStage === 'error' ? <div className="lotus-pay-test-status is-error" role="alert"><div><strong>We couldn’t confirm the payment</strong><p>{paymentTest.error}</p></div></div> : null}
          {paybillTestStage === 'idle' ? <p className="lotus-pay-test-help">Enter the number exactly as registered with M-Pesa. You’ll receive the STK prompt after selecting <strong>Send payment prompt</strong>.</p> : null}
          <div className="form-actions">{paybillTestStage === 'idle' ? <><Button type="button" variant="ghost" onClick={() => setPaybillTestModalOpen(false)}>Cancel</Button><Button type="submit" icon={Smartphone}>Send payment prompt</Button></> : null}{['success', 'error'].includes(paybillTestStage) ? <Button type="button" onClick={() => setPaybillTestModalOpen(false)}>Close</Button> : null}</div>
        </form>
      </Modal>
    </div>
  );
}
